import assert from 'assert';
import { ScanUploadState } from '@/views/case-settings/upload/shared/ScanUploadState';
import { DicomInfoFactory } from '@/lib/dicom/DicomInfoFactory';
import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import { validateDicoms } from '@/lib/dicom/validateDicoms';
import { validatePatientIdentities } from '@/lib/dicom/validatePatientIdentities';
import { groupDicomSeries } from '@/lib/dicom/groupDicomSeries';
import { comparePatient } from '@/views/case-settings/upload/shared/comparePatient';
import type { CasePatient } from '@/stores/caseSettings/types';
import { DicomMessages } from '@/lib/dicom/dicomMessages';
import { DicomMessageLevel, type DicomSeries } from '@/lib/dicom/DicomSeries';
import DicomUtils from '@/lib/dicom/DicomUtil';
import uploadWorker from '@/views/case-settings/webworkers/uploadWorker.ts?worker&url';
import { apiBaseUrl } from '@/util/headMetaTags';
import { UploadEvent } from '@/views/case-settings/webworkers/types';
import {
    cancelUpload,
    handleUploadError,
    makeInitialUploadState,
    startUpload,
} from '@/views/case-settings/webworkers/uploadWorkerUtils';
import { dicomPatientInfo } from '@/views/case-settings/upload/shared/dicomUploadFlags';
import type { DicomUploadContext } from '@/views/case-settings/upload/shared/useDicomUploadLifecycle';

async function start<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
    filesIn: TInput[],
): Promise<void> {
    reset(ctx);
    try {
        ctx.state.value.state = ScanUploadState.CollectingData;
        ctx.inputs.value = filesIn;

        // Series selection (WI-919) is CT-only — x-ray flows have their own
        // upload zone with no selection modal, so the lifecycle must skip
        // straight to the direct-parse path for them.
        if (ctx.config.modality === 'ct-scan') {
            await startWithSeriesSelection(ctx, filesIn);
        } else {
            await startDirect(ctx, filesIn);
        }
    } catch (error) {
        handleUploadError(error, ctx.state);
    }
}

async function startDirect<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
    filesIn: TInput[],
): Promise<void> {
    const dicoms = await Promise.all(
        filesIn.map((input) => DicomInfoFactory.makeDicomInfo(ctx.config.extractFile(input))),
    );
    ctx.validatedDicoms.value = dicoms;

    await processDicoms(ctx, dicoms);

    if (ctx.state.value.state === ScanUploadState.CollectingData) {
        await decideUploadOrConfirm(ctx);
    }
}

async function startWithSeriesSelection<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
    filesIn: TInput[],
): Promise<void> {
    const rawFiles = filesIn.map((input) => ctx.config.extractFile(input));
    ctx.seriesGroups.value = await groupDicomSeries(rawFiles);

    const groups = Object.values(ctx.seriesGroups.value);
    if (groups.length === 1) {
        await runValidation(ctx, groups[0]);
        return;
    }
    // Zero or many surviving series: let the user resolve it via the modal.
    // The modal renders an empty-state message when seriesGroups is empty.
    ctx.state.value.state = ScanUploadState.WaitingForSeriesSelection;
}

async function selectSeries<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
    seriesInstanceUid: string,
): Promise<void> {
    try {
        assert.ok(
            ctx.state.value.state === ScanUploadState.WaitingForSeriesSelection,
            'selectSeries called outside the series selection state',
        );
        const picked = ctx.seriesGroups.value[seriesInstanceUid];
        assert.ok(picked, `no series group found for ${seriesInstanceUid}`);
        await runValidation(ctx, picked);
    } catch (error) {
        handleUploadError(error, ctx.state);
    }
}

async function runValidation<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
    picked: DicomSeries,
): Promise<void> {
    ctx.state.value.state = ScanUploadState.CollectingData;
    ctx.validatedDicoms.value = picked.items;

    const pickedFiles = new Set(picked.items.map((d) => d.file));
    ctx.inputs.value = ctx.inputs.value.filter((i) => pickedFiles.has(ctx.config.extractFile(i)));

    await processDicoms(ctx, picked.items);

    if (ctx.state.value.state === ScanUploadState.CollectingData) {
        await decideUploadOrConfirm(ctx);
    }
}

async function processDicoms<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
    dicoms: DicomInfo[],
): Promise<void> {
    ctx.state.value.patientIdentityCheck = await validatePatientIdentities(dicoms);
    if (!ctx.state.value.patientIdentityCheck.isValid) {
        ctx.state.value.state = ScanUploadState.PatientInconsistentIdentityError;
        return;
    }

    ctx.processDicomsResult.value = await validateDicoms(dicoms, ctx.config.modality, {
        requireSingleSeries: ctx.config.requireSingleSeries,
    });
    if (!ctx.processDicomsResult.value.isValid) {
        ctx.state.value.state = ScanUploadState.InModalError;
        ctx.state.value.error = 'Finding one validated series failed';
        return;
    }

    const casePatient: CasePatient = ctx.caseSettings.case.patient;
    const comparison = comparePatient(casePatient, dicomPatientInfo(ctx));
    if (!comparison.isSame) {
        ctx.state.value.patientComparison = comparison;
        ctx.state.value.state = ScanUploadState.EnteredPatientVsDicomDataMismatch;
    }
}

async function decideUploadOrConfirm<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
): Promise<void> {
    assert.ok(ctx.processDicomsResult.value, 'no validation result');
    const candidates = ctx.processDicomsResult.value.uploadCandidates;
    assert.ok(candidates.length > 0, 'no candidate');

    const allOk = candidates.every((c) => DicomUtils.isSeriesOkForAutoUpload(c.series));
    if (allOk) {
        for (const c of candidates) {
            DicomMessages.appendMessage(
                c.series.messages,
                DicomMessageLevel.Info,
                ctx.config.messages.autoUpload,
            );
        }
        await performUpload(ctx);
    } else {
        ctx.state.value.state = ScanUploadState.WaitingForUploadConfirmation;
    }
}

async function performUpload<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
): Promise<void> {
    ctx.state.value.state = ScanUploadState.UploadingData;

    assert.ok(ctx.processDicomsResult.value, 'no validation result');
    const candidates = ctx.processDicomsResult.value.uploadCandidates;
    assert.ok(candidates.length > 0, 'no candidate');

    const caseId = ctx.caseSettings.case.id;
    assert.ok(caseId != null, 'case id is not defined');

    const studyId = await ctx.config.createStudy({
        caseId: Number(caseId),
        candidates,
        inputs: ctx.inputs.value,
    });

    const workerFiles = ctx.config.buildWorkerPayload({ candidates, inputs: ctx.inputs.value });

    await startUpload(
        {
            workerUrl: uploadWorker,
            caseId: Number(caseId),
            studyId,
            workerMessage: {
                baseUrl: apiBaseUrl(),
                caseId: String(caseId),
                studyId,
                files: workerFiles,
                event: UploadEvent.Start,
                uploadType: ctx.config.uploadType,
            },
            fileCount: workerFiles.length,
            onComplete: () => {
                ctx.config.onUploadComplete?.({ candidates, inputs: ctx.inputs.value });
            },
        },
        ctx.state,
    );
}

async function uploadAnyway<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
): Promise<void> {
    assert.ok(
        ctx.state.value.state === ScanUploadState.EnteredPatientVsDicomDataMismatch,
        'wrong state',
    );

    if (!ctx.processDicomsResult.value && ctx.validatedDicoms.value.length > 0) {
        ctx.processDicomsResult.value = await validateDicoms(
            ctx.validatedDicoms.value,
            ctx.config.modality,
            { requireSingleSeries: ctx.config.requireSingleSeries },
        );
        if (!ctx.processDicomsResult.value.isValid) {
            ctx.state.value.state = ScanUploadState.InModalError;
            ctx.state.value.error = 'Finding one validated series failed';
            return;
        }
    }

    assert.ok(ctx.processDicomsResult.value, 'no validation result');
    for (const c of ctx.processDicomsResult.value.uploadCandidates) {
        DicomMessages.appendMessage(
            c.series.messages,
            DicomMessageLevel.Info,
            ctx.config.messages.uploadDespiteMismatch,
        );
    }
    await performUpload(ctx);
}

async function userConfirmationUpload<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
): Promise<void> {
    assert.ok(ctx.processDicomsResult.value, 'no validation result');
    for (const c of ctx.processDicomsResult.value.uploadCandidates) {
        DicomMessages.appendMessage(
            c.series.messages,
            DicomMessageLevel.Info,
            ctx.config.messages.userConfirmed,
        );
    }
    await performUpload(ctx);
}

function cancel<TInput, TPayload>(ctx: DicomUploadContext<TInput, TPayload>): void {
    cancelUpload(ctx.state);
}

function reset<TInput, TPayload>(ctx: DicomUploadContext<TInput, TPayload>): void {
    ctx.state.value = makeInitialUploadState();
    ctx.processDicomsResult.value = null;
    ctx.validatedDicoms.value = [];
    ctx.inputs.value = [];
    ctx.seriesGroups.value = {};
}

/**
 * Bind the upload lifecycle's actions to a shared {@link DicomUploadContext}.
 * Each returned action keeps the exact behaviour of the inline composable
 * actions it replaces; this factory only closes them over `ctx` so the
 * composable body stays within the function-length cap.
 */
export function createDicomUploadActions<TInput, TPayload>(
    ctx: DicomUploadContext<TInput, TPayload>,
) {
    return {
        start: (filesIn: TInput[]) => start(ctx, filesIn),
        selectSeries: (seriesInstanceUid: string) => selectSeries(ctx, seriesInstanceUid),
        uploadAnyway: () => uploadAnyway(ctx),
        userConfirmationUpload: () => userConfirmationUpload(ctx),
        cancel: () => cancel(ctx),
        reset: () => reset(ctx),
    };
}